import { useSyncExternalStore } from "react";
import { hi } from "./hi";

/*
 * The screens' language, and how they write numbers and dates. No library: a dictionary per language, keyed by the
 * English text itself, so a string nobody translated yet simply shows in English.
 *
 *   t("Save")                         "सहेजें" in Hindi, "Save" in English
 *   t("{n} invoices", { n: 3 })       placeholders in braces
 *   useLocale()                       in a component: re-renders it when the language or a format changes
 *   setLocale({ language, ... })      what /admin/locale/me answered (Shell loads it after sign-in)
 *
 * The shared parts in ui/parts.tsx (page heads, tabs, fields, facts, drawers, dialogs, stats) pass their labels
 * through t() themselves, so a screen built from them gets its headings translated without changes. Text a screen
 * writes itself (buttons, table headers) is wrapped in t() where it is written. Adding a language: a dictionary like
 * mr.ts, its loader in CHUNKS, and the same language on the server (server/i18n.js LANGUAGES).
 *
 * Hindi, Marathi and Tamil (wave 7). Marathi and Tamil are each one chunk (mr.ts, ta.ts), fetched the first time the
 * language is chosen. A reviewer's corrections (Customization > Language & formats > Translations) arrive through
 * setOverrides() and win over the shipped text. Digits stay Latin in every language; dates use the language's month names.
 */

export type Lang = "en" | "hi" | "mr" | "ta" | "bn" | "gu" | "pa" | "kn" | "ml" | "or" | "te";

/*
 * Wave 8: Bengali, Gujarati, Punjabi, Kannada, Malayalam, Odia and Telugu are MACHINE-TRANSLATED (a model wrote the main
 * screens' texts, not reviewed by a native speaker); a screen they do not cover shows in English. The language lists name them
 * with langLabel(), which says so.
 */
export const MACHINE: ReadonlySet<Lang> = new Set<Lang>(["bn", "gu", "pa", "kn", "ml", "or", "te"]);
export type NumberFormat = "indian" | "international";
export type DateFormat = "dd/mm/yyyy" | "dd-mm-yyyy" | "dd mmm yyyy" | "mm/dd/yyyy" | "yyyy-mm-dd";
export type Locale = { language: Lang; number_format: NumberFormat; date_format: DateFormat };

const DICTS: Record<Lang, Record<string, string>> = { en: {}, hi, mr: {}, ta: {}, bn: {}, gu: {}, pa: {}, kn: {}, ml: {}, or: {}, te: {} };
const OVERRIDES: Partial<Record<Lang, Record<string, string>>> = {};

let current: Locale = { language: "en", number_format: "indian", date_format: "dd mmm yyyy" };
let version = 0;
const listeners = new Set<() => void>();
const changed = () => {
  version++;
  listeners.forEach((l) => l());
};

/*
 * The rest of the screens' Hindi (hi-screens.ts, wave 6: about 4,000 texts) is its own chunk, fetched the first time Hindi
 * is chosen, so English sessions do not download it. Until it arrives those texts show in English; then every screen
 * re-renders. hi.ts (the core screens) wins where both have a text. The service worker keeps the chunk for offline use.
 */
const CHUNKS: Partial<Record<Lang, () => Promise<Record<string, string>>>> = {
  hi: () => import("./hi-screens").then((m) => ({ ...m.hiScreens, ...hi })),
  mr: () => import("./mr").then((m) => m.mr),
  ta: () => import("./ta").then((m) => m.ta),
  bn: () => import("./bn").then((m) => m.bn),
  gu: () => import("./gu").then((m) => m.gu),
  pa: () => import("./pa").then((m) => m.pa),
  kn: () => import("./kn").then((m) => m.kn),
  ml: () => import("./ml").then((m) => m.ml),
  or: () => import("./or").then((m) => m.or),
  te: () => import("./te").then((m) => m.te),
};
const loading: Partial<Record<Lang, Promise<void>>> = {};
function loadScreens(lang: Lang) {
  const load = CHUNKS[lang];
  if (!load) return;
  loading[lang] ??= load().then(
    (d) => {
      DICTS[lang] = d;
      changed();
    },
    () => {
      delete loading[lang];
    },
  );
}

/** A reviewer's corrections for a language (/admin/locale/overrides/<lang>): they win over the shipped text. */
export function setOverrides(lang: Lang, texts: Record<string, string>) {
  OVERRIDES[lang] = texts;
  if (lang === current.language) changed();
}

/** The browser's first language the CRM has (navigator.languages), for the sign-in page before anyone is known. */
export function browserLang(): Lang {
  const list = typeof navigator === "undefined" ? [] : (navigator.languages?.length ? navigator.languages : [navigator.language]);
  for (const tag of list) {
    const code = String(tag).toLowerCase().split("-")[0];
    if (code in DICTS) return code as Lang;
  }
  return "en";
}

export function setLocale(next: Partial<Locale>) {
  const merged = { ...current, ...next };
  if (!(merged.language in DICTS)) merged.language = "en";
  if (merged.language === current.language && merged.number_format === current.number_format && merged.date_format === current.date_format) return;
  current = merged;
  document.documentElement.lang = merged.language;
  loadScreens(merged.language);
  changed();
}

export const getLocale = () => current;

/** The text in the current language; English (and anything not in the dictionary) as it is. */
export function t(key: string, vars?: Record<string, string | number | boolean | null | undefined>): string {
  const s = OVERRIDES[current.language]?.[key] ?? DICTS[current.language]?.[key] ?? key;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : s;
}

/** A language's name for a language list: "বাংলা · machine-translated" for the languages a model translated. */
export const langLabel = (code: string, name: string) => (MACHINE.has(code as Lang) ? t("{name} · machine-translated", { name }) : name);

/** t() for a value that may not be a string (a label prop that is a node): strings are translated, the rest is left alone. */
export const tx = <T,>(v: T): T => (typeof v === "string" ? (t(v) as T) : v);

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Called on every change of language or format (the phone app keeps the choice for the offline screen). */
export const subscribeLocale = subscribe;

/** Re-renders the component when the locale changes; returns it. */
export function useLocale(): Locale {
  useSyncExternalStore(subscribe, () => version);
  return current;
}

/*
 * The BCP 47 tag for Intl: numbers use en-IN for lakh/crore grouping and en-US for international; dates the language's own
 * month names, with Latin digits (Marathi's default numbering is Devanagari, which invoices and GST returns do not use).
 */
const DATE_TAGS: Record<Lang, string> = { en: "en-IN", hi: "hi-IN", mr: "mr-IN-u-nu-latn", ta: "ta-IN-u-nu-latn", bn: "bn-IN-u-nu-latn", gu: "gu-IN-u-nu-latn", pa: "pa-IN-u-nu-latn", kn: "kn-IN-u-nu-latn", ml: "ml-IN-u-nu-latn", or: "or-IN-u-nu-latn", te: "te-IN-u-nu-latn" };
export const intlTag = (kind: "number" | "date" = "date") => (kind === "number" ? (current.number_format === "international" ? "en-US" : "en-IN") : DATE_TAGS[current.language]);

/*
 * Wave 9: some browsers' Intl has no dates for a language (Punjabi gives "M10 31", Odia falls back to US English). For those,
 * the date is formatted in Indian English and its month and weekday names put in the language's own (CLDR's; the short months
 * are the ones server/i18n.js prints on documents).
 */
const NAMES: Partial<Record<Lang, { months: string[]; short: string[]; days: string[]; shortDays: string[] }>> = {
  pa: {
    months: ["ਜਨਵਰੀ", "ਫ਼ਰਵਰੀ", "ਮਾਰਚ", "ਅਪ੍ਰੈਲ", "ਮਈ", "ਜੂਨ", "ਜੁਲਾਈ", "ਅਗਸਤ", "ਸਤੰਬਰ", "ਅਕਤੂਬਰ", "ਨਵੰਬਰ", "ਦਸੰਬਰ"],
    short: ["ਜਨ", "ਫ਼ਰ", "ਮਾਰਚ", "ਅਪ੍ਰੈ", "ਮਈ", "ਜੂਨ", "ਜੁਲਾ", "ਅਗ", "ਸਤੰ", "ਅਕਤੂ", "ਨਵੰ", "ਦਸੰ"],
    days: ["ਐਤਵਾਰ", "ਸੋਮਵਾਰ", "ਮੰਗਲਵਾਰ", "ਬੁੱਧਵਾਰ", "ਵੀਰਵਾਰ", "ਸ਼ੁੱਕਰਵਾਰ", "ਸ਼ਨਿੱਚਰਵਾਰ"],
    shortDays: ["ਐਤ", "ਸੋਮ", "ਮੰਗਲ", "ਬੁੱਧ", "ਵੀਰ", "ਸ਼ੁੱਕਰ", "ਸ਼ਨਿੱਚਰ"],
  },
  or: {
    months: ["ଜାନୁଆରୀ", "ଫେବୃଆରୀ", "ମାର୍ଚ୍ଚ", "ଅପ୍ରେଲ", "ମଇ", "ଜୁନ", "ଜୁଲାଇ", "ଅଗଷ୍ଟ", "ସେପ୍ଟେମ୍ବର", "ଅକ୍ଟୋବର", "ନଭେମ୍ବର", "ଡିସେମ୍ବର"],
    short: ["ଜାନୁଆରୀ", "ଫେବୃଆରୀ", "ମାର୍ଚ୍ଚ", "ଅପ୍ରେଲ", "ମଇ", "ଜୁନ", "ଜୁଲାଇ", "ଅଗଷ୍ଟ", "ସେପ୍ଟେମ୍ବର", "ଅକ୍ଟୋବର", "ନଭେମ୍ବର", "ଡିସେମ୍ବର"],
    days: ["ରବିବାର", "ସୋମବାର", "ମଙ୍ଗଳବାର", "ବୁଧବାର", "ଗୁରୁବାର", "ଶୁକ୍ରବାର", "ଶନିବାର"],
    shortDays: ["ରବି", "ସୋମ", "ମଙ୍ଗଳ", "ବୁଧ", "ଗୁରୁ", "ଶୁକ୍ର", "ଶନି"],
  },
};
const knows = new Map<Lang, boolean>();
function intlKnows(lang: Lang) {
  if (!knows.has(lang)) {
    const probe = new Date(Date.UTC(2026, 9, 15)).toLocaleDateString(DATE_TAGS[lang], { month: "long", timeZone: "UTC" });
    knows.set(lang, Intl.DateTimeFormat(DATE_TAGS[lang]).resolvedOptions().locale.split("-")[0] === lang && !/^M\d|October/.test(probe));
  }
  return knows.get(lang);
}

/** A date (and time, with `withTime`) in the screen's language: Intl where the browser has the language, else its own names. */
export function localDate(d: Date, opts: Intl.DateTimeFormatOptions, withTime = false): string {
  const lang = current.language;
  const names = NAMES[lang];
  if (!names || intlKnows(lang)) return withTime ? d.toLocaleString(intlTag(), opts) : d.toLocaleDateString(intlTag(), opts);
  const tz = opts.timeZone;
  const month = Number(new Intl.DateTimeFormat("en-IN", { month: "numeric", timeZone: tz }).format(d)) - 1;
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: tz }).format(d));
  return new Intl.DateTimeFormat("en-IN", opts)
    .formatToParts(d)
    .map((p) => (p.type === "month" && !/^\d+$/.test(p.value) ? (opts.month === "long" ? names.months : names.short)[month] : p.type === "weekday" ? (opts.weekday === "long" ? names.days : names.shortDays)[day] : p.value))
    .join("");
}
